import { useState } from "react";
import {
  SimpleShowLayout,
  TextField,
  BooleanField,
  DateField,
  Title,
  useGetIdentity,
  usePermissions,
  useAuthProvider,
  useNotify,
  TopToolbar,
  RefreshButton,
  Button,
  RecordContextProvider,
} from "react-admin";
import { Box, Switch, Typography, Grid2 as Grid, Chip, Link, Stack, Divider, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useClarityConsentSettings } from "@/hooks/useClarity";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { AlertType, Permission, UserStatus } from "@/types";
import { AssignedRole } from "@/types/role";
import { SectionCard, InfoRow } from "@/components/common/SectionCard";
import { PageContent } from "@/components/common/PageLayout";
import { PermissionsDisplay } from "./roles/components/PermissionsDisplay";
import { AUTH_CONSTANTS } from "@/constants";

const PermissionActions = () => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const authProvider = useAuthProvider();

  const clearStoredData = () => {
    const keys = AUTH_CONSTANTS.STORAGE_KEYS;

    sessionStorage.removeItem(keys.AUTH_ME_DATA);
    sessionStorage.removeItem(keys.RA_PERMISSIONS);

    notify("Cleared all stored authentication data", { type: AlertType.SUCCESS });
    window.location.reload();
  };

  const refreshData = async () => {
    try {
      if (authProvider?.getIdentity) {
        await authProvider.getIdentity();
      }
      if (authProvider?.getPermissions) {
        await authProvider.getPermissions({});
      }
      notify("Refreshed authentication data", { type: AlertType.SUCCESS });
      window.location.reload();
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <TopToolbar>
      <RefreshButton onClick={refreshData} />
      <Button label="Clear Storage" onClick={clearStoredData} startIcon={<DeleteIcon />} />
    </TopToolbar>
  );
};

export const AuthInfoPage = () => {
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const { permissions } = usePermissions();
  const { clarityEnabled, consentGiven, updateConsent } = useClarityConsentSettings();
  const [showRawIdentity, setShowRawIdentity] = useState(false);

  // Convert permissions object to Permission[] for PermissionsDisplay
  const permissionArray: Permission[] = [];
  if (permissions) {
    Object.entries(permissions).forEach(([resource, actions]) => {
      if (typeof actions === "object" && actions !== null) {
        Object.entries(actions).forEach(([action, allowed]) => {
          if (allowed) {
            permissionArray.push({
              id: `${resource}-${action}`,
              resource,
              action,
              name: `${action} ${resource}`,
            } as Permission);
          }
        });
      }
    });
  }

  if (identityLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Title title="Authentication Information" />
      <PermissionActions />

      <PageContent>
        {/* User Summary */}
        {identity && (
          <SectionCard title="User Summary">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <InfoRow label="Full Name" value={identity.fullName || "-"} />
                <InfoRow label="Email" value={identity.email || "-"} />
                <InfoRow label="Job Title" value={identity.jobTitle || "-"} />
                <InfoRow label="Department" value={identity.department || "-"} />
                <InfoRow
                  label="Status"
                  value={
                    <Chip
                      label={identity.status || "unknown"}
                      color={identity.status === UserStatus.ACTIVE ? "success" : "default"}
                      size="small"
                    />
                  }
                />
                <InfoRow label="Account Type" value={identity.accountType || "-"} divider={false} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InfoRow label="Timezone" value={identity.timezone || "-"} />
                <InfoRow label="Locale" value={identity.locale || "-"} />
                <InfoRow label="Preferred Language" value={identity.preferredLanguage || "-"} />
                <InfoRow
                  label="Email Verified"
                  value={
                    <Chip
                      label={identity.emailVerified ? "Yes" : "No"}
                      color={identity.emailVerified ? "success" : "default"}
                      size="small"
                    />
                  }
                />
                <InfoRow
                  label="Phone Verified"
                  value={
                    <Chip
                      label={identity.phoneVerified ? "Yes" : "No"}
                      color={identity.phoneVerified ? "success" : "default"}
                      size="small"
                    />
                  }
                  divider={false}
                />
              </Grid>
            </Grid>
          </SectionCard>
        )}

        {/* Analytics Preferences */}
        {clarityEnabled && (
          <SectionCard title="Privacy & Analytics">
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <BarChartIcon color={consentGiven ? "primary" : "disabled"} sx={{ mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="subtitle1">Session Analytics</Typography>
                  <Switch checked={consentGiven} onChange={(e) => updateConsent(e.target.checked)} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Help us improve Aegle Claims Platform by allowing Microsoft Clarity to record anonymized session data.
                  No sensitive claim or policy information is captured.{" "}
                  <Link
                    href="https://learn.microsoft.com/en-us/clarity/faq"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 500 }}
                  >
                    Learn more
                  </Link>
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        )}

        {/* Roles & Permissions */}
        {(permissionArray.length > 0 || (identity?.assignedRoles && identity.assignedRoles.length > 0)) && (
          <SectionCard title="Roles & Permissions" subtitle="Effective permissions computed from assigned roles">
            {identity?.assignedRoles && identity.assignedRoles.length > 0 && (
              <Box sx={{ px: 1, pb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Assigned Roles
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                  {identity.assignedRoles.map((item: AssignedRole, index: number) => (
                    <Tooltip key={index} title={item.role.description || ""} arrow>
                      <Chip label={item.role.name} color="primary" variant="outlined" />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}
            {identity?.assignedRoles && identity.assignedRoles.length > 0 && permissionArray.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            {permissionArray.length > 0 && <PermissionsDisplay permissions={permissionArray} />}
          </SectionCard>
        )}

        {/* Raw Identity Data (Debug) */}
        {identity && (
          <SectionCard
            title="Raw Identity Data"
            subtitle="Complete identity object from the API"
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <Switch checked={showRawIdentity} onChange={(e) => setShowRawIdentity(e.target.checked)} size="small" />
              </Box>
            }
          >
            {showRawIdentity ? (
              <RecordContextProvider value={identity}>
                <SimpleShowLayout>
                  <TextField source="id" />
                  <TextField source="aegleId" />
                  <TextField source="fullName" />
                  <TextField source="email" />
                  <TextField source="uniqueName" />
                  <TextField source="userPrincipalName" />
                  <TextField source="preferredName" />
                  <TextField source="phone" />
                  <TextField source="department" />
                  <TextField source="jobTitle" />
                  <TextField source="managerUserIdRef" />
                  <TextField source="timezone" />
                  <TextField source="locale" />
                  <TextField source="preferredLanguage" />
                  <TextField source="status" />
                  <TextField source="accountType" />
                  <BooleanField source="emailVerified" />
                  <BooleanField source="phoneVerified" />
                  <TextField source="partnerId" />
                </SimpleShowLayout>
              </RecordContextProvider>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Toggle to view raw identity data for debugging
              </Typography>
            )}
          </SectionCard>
        )}

        {/* Metadata */}
        {identity?.metadata && showRawIdentity && (
          <SectionCard title="Metadata">
            <RecordContextProvider value={identity.metadata}>
              <SimpleShowLayout>
                <DateField source="retrieved_datetime" label="Retrieved" showTime />
                <TextField source="permission_count" label="Permission Count" />
                <TextField source="role_count" label="Role Count" />
              </SimpleShowLayout>
            </RecordContextProvider>
          </SectionCard>
        )}
      </PageContent>
    </Box>
  );
};

export default AuthInfoPage;
